import { RbcStatus, RbcUpdateRecStatusEnum } from '@calm/evexia-web-sdk';
import { DAY_S } from '@calm/web-common/dist/time';
import createRbcApi from '@rhg/libs/api/create-rbc-api';
import useAuth from '@rhg/libs/auth0/use-auth';
import { useMutation } from '@rhg/libs/react-query';
import { useActiveCircle } from '../use-active-circle';
import { useForceRefreshCircleRecommendations } from '../use-recommendations/use-force-refresh-circle-recommendations';

export const getRbcUpdateRecStatusEnum = (
    status: RbcStatus
): RbcUpdateRecStatusEnum | undefined => {
    switch (status) {
        case RbcStatus.Viewed:
            return RbcUpdateRecStatusEnum.Viewed;
        case RbcStatus.Declined:
            return RbcUpdateRecStatusEnum.Declined;
        case RbcStatus.InProgress:
            return RbcUpdateRecStatusEnum.InProgress;
        case RbcStatus.Snoozed:
            return RbcUpdateRecStatusEnum.Snoozed;
        case RbcStatus.Completed:
            return RbcUpdateRecStatusEnum.Completed;
        default:
            return undefined;
    }
};

export const defaultSnoozeDuration = 10 * DAY_S;

export interface UpdateCircleRecommendationStatusParams {
    status: RbcStatus;
    snoozeDuration?: number;
    willRefreshRecommendations?: boolean;
}

export function useUpdateCircleRecommendationStatus(
    /** aka recDefnId */
    recommendationId: string | undefined
) {
    const { activeCircle: circle } = useActiveCircle();
    const { getAccessTokenSilently } = useAuth();
    const { forceRefreshRecommendations } =
        useForceRefreshCircleRecommendations();

    const { mutate, mutateAsync, ...otherProps } = useMutation({
        mutationFn: async ({
            status,
            snoozeDuration = defaultSnoozeDuration,
        }: UpdateCircleRecommendationStatusParams) => {
            const api = createRbcApi(getAccessTokenSilently);
            const updateRecStatusEnum = getRbcUpdateRecStatusEnum(status);

            if (
                typeof circle?.recipient.id === 'number' &&
                typeof circle.authorizationJWT === 'string' &&
                typeof recommendationId === 'string' &&
                !!updateRecStatusEnum
            ) {
                await api.updateV1RecommendationsCircle({
                    circleAuthorizationToken: circle.authorizationJWT,
                    recipientId: circle.recipient.id,
                    recommendationDefnId: recommendationId,
                    rbcUpdateRecRequest: {
                        status: updateRecStatusEnum,
                        snoozeDuration:
                            updateRecStatusEnum === RbcStatus.Snoozed
                                ? snoozeDuration
                                : undefined,
                    },
                });
            }
        },
        onSuccess: (_, { willRefreshRecommendations = true }) => {
            if (willRefreshRecommendations) {
                forceRefreshRecommendations({ circle });
            }
        },
    });

    return {
        updateStatus: mutate,
        updateStatusAsync: mutateAsync,
        ...otherProps,
    };
}
